import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import CustomTextInput from '../../../components/CustomTextInput';
import SelectedItems from '../../../components/SelectedItems';
import { isPartnerEmbeddedAdmin } from '../../../utils/roles';
import {
  ACCESS_LEVEL_OPTIONS,
  ACCESS_LEVELS,
  BUSINESS_PROFILE_ACCESS_LEVEL_OPTIONS,
  BUSINESS_PROFILES_MODULE_KEY,
  DEVELOPER_DASHBOARD_MODULE_KEY,
  EINVOICING_MODULE_KEYS,
  applyDeveloperDashboardSelection,
  applyEinvoicingModuleSelection,
  buildDefaultPermissions,
  getVisibleRoleModules,
  isEinvoicingModuleActive,
  mapToPermissionsPayload,
  permissionsToMap,
} from '../constants/roleModules';
import { useCreateRoleHandler } from '../dataHandlers/createRoleHandler';
import { useUpdateRoleHandler } from '../dataHandlers/updateRoleHandler';
import { useGetBusinessProfilesHandler } from '../../../dataHandlers/getBusinessProfilesHandler';
import BusinessProfileMultiSelect from './BusinessProfileMultiSelect';

const getProfileChipLabel = (profiles, profileId) => {
  const profile = profiles.find((item) => item.profile_id === profileId);
  if (!profile) return profileId;
  const name = profile.name || '';
  const tin = profile.tin || '';
  return [name, tin].filter(Boolean).join(' | ') || profileId;
};

const modalPaperSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 560 },
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  p: 2,
  outline: 'none',
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
};

export default function RoleFormModal({ open, onClose, mode, roleData }) {
  const currentUser = useSelector((state) => state.user.user);
  const partnerDashboardOnly = isPartnerEmbeddedAdmin(currentUser);
  const visibleModules = useMemo(() => getVisibleRoleModules(partnerDashboardOnly), [partnerDashboardOnly]);

  const isView = mode === 'view';
  const isCreate = mode === 'create';

  const schema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
  });

  const [permissionMap, setPermissionMap] = useState(buildDefaultPermissions());
  const [allowedBusinessProfileIds, setAllowedBusinessProfileIds] = useState([]);
  const [businessProfilesTouched, setBusinessProfilesTouched] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { name: '' },
  });

  const { profiles, isLoading: isProfilesLoading } = useGetBusinessProfilesHandler({
    searchQuery: { page: 0, size: 100 },
    enabled: open && !partnerDashboardOnly,
  });

  useEffect(() => {
    if (open) {
      const nextPermissions = roleData?.permissions
        ? permissionsToMap(roleData.permissions)
        : buildDefaultPermissions();
      if (partnerDashboardOnly) {
        setPermissionMap(
          applyDeveloperDashboardSelection(
            nextPermissions,
            nextPermissions[DEVELOPER_DASHBOARD_MODULE_KEY] ?? ACCESS_LEVELS.NONE,
          ),
        );
        setAllowedBusinessProfileIds([]);
      } else {
        setPermissionMap(nextPermissions);
        setAllowedBusinessProfileIds(roleData?.business_profile_ids ?? []);
      }
      setBusinessProfilesTouched(false);
      reset({ name: roleData?.name ?? '' });
    }
  }, [open, roleData, reset, partnerDashboardOnly]);

  const { mutate: createRole, isPending: isCreating } = useCreateRoleHandler({ onSuccess: onClose });
  const { mutate: updateRole, isPending: isUpdating } = useUpdateRoleHandler({ onSuccess: onClose });

  const title = isCreate ? 'Create Role' : isView ? 'View Role' : 'Edit Role';

  const onSubmit = (data) => {
    const permissions = partnerDashboardOnly
      ? mapToPermissionsPayload(
          applyDeveloperDashboardSelection(
            permissionMap,
            permissionMap[DEVELOPER_DASHBOARD_MODULE_KEY] ?? ACCESS_LEVELS.NONE,
          ),
        )
      : mapToPermissionsPayload(permissionMap);
    const payload = {
      name: data.name.trim(),
      permissions,
      business_profile_ids: partnerDashboardOnly ? [] : allowedBusinessProfileIds,
    };
    if (isCreate) {
      createRole(payload);
      return;
    }
    updateRole({ roleId: roleData.id, payload });
  };

  const isPending = isCreating || isUpdating;
  const businessProfilesAccessLevel = permissionMap[BUSINESS_PROFILES_MODULE_KEY] || ACCESS_LEVELS.READ;
  const developerDashboardLevel = permissionMap[DEVELOPER_DASHBOARD_MODULE_KEY] || ACCESS_LEVELS.NONE;
  const isDeveloperDashboardActive = developerDashboardLevel > ACCESS_LEVELS.NONE;
  const isEinvoicingActive =
    !partnerDashboardOnly &&
    (isEinvoicingModuleActive(permissionMap) ||
      allowedBusinessProfileIds.length > 0 ||
      businessProfilesAccessLevel === ACCESS_LEVELS.WRITE);
  const isBusinessProfileSelectDisabled = isView || isDeveloperDashboardActive;
  const isBusinessProfileAccessDisabled = isView || isDeveloperDashboardActive;
  const isDeveloperDashboardDisabled = isView || isEinvoicingActive;
  const isBusinessProfilesRequired = !partnerDashboardOnly && !isBusinessProfileSelectDisabled;
  const showBusinessProfilesError =
    isBusinessProfilesRequired && businessProfilesTouched && allowedBusinessProfileIds.length === 0;
  const isSubmitDisabled = isPending || (isBusinessProfilesRequired && allowedBusinessProfileIds.length === 0);

  const handleAccessChange = (moduleKey, value) => {
    if (moduleKey === DEVELOPER_DASHBOARD_MODULE_KEY) {
      setPermissionMap((prev) => applyDeveloperDashboardSelection(prev, value));
      if (value > ACCESS_LEVELS.NONE) {
        setAllowedBusinessProfileIds([]);
      }
      return;
    }

    if (moduleKey === BUSINESS_PROFILES_MODULE_KEY) {
      setPermissionMap((prev) => applyEinvoicingModuleSelection(prev, moduleKey, value));
      return;
    }

    if (EINVOICING_MODULE_KEYS.includes(moduleKey)) {
      setPermissionMap((prev) => applyEinvoicingModuleSelection(prev, moduleKey, value));
    }
  };

  const handleBusinessProfileIdsChange = (ids) => {
    setBusinessProfilesTouched(true);
    setAllowedBusinessProfileIds(ids);
    if (ids.length > 0) {
      setPermissionMap((prev) => ({
        ...prev,
        [DEVELOPER_DASHBOARD_MODULE_KEY]: ACCESS_LEVELS.NONE,
      }));
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper elevation={0} sx={modalPaperSx}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Divider sx={{ my: 1 }} />

        <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Role Name
              </Typography>
              <CustomTextInput
                name="name"
                label=""
                control={control}
                errors={errors}
                readOnly={isView}
                placeholder="e.g. Sales Manager"
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Permissions
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {visibleModules.map(({ key, label, tooltip, infoTooltip }, index) => {
                  const isEinvoicingModule = EINVOICING_MODULE_KEYS.includes(key);
                  const isModuleDisabled =
                    isView ||
                    (key === DEVELOPER_DASHBOARD_MODULE_KEY && isDeveloperDashboardDisabled) ||
                    (isEinvoicingModule && isDeveloperDashboardActive);

                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        borderBottom: index < visibleModules.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">{label || key}</Typography>
                        {infoTooltip && tooltip && (
                          <Tooltip title={tooltip} arrow>
                            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Stack>
                      <FormControl size="small" sx={{ minWidth: 160 }} disabled={isModuleDisabled}>
                        <Select
                          value={permissionMap[key] ?? 0}
                          onChange={(e) => handleAccessChange(key, e.target.value)}
                          sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                        >
                          {ACCESS_LEVEL_OPTIONS.map(({ value, label: optionLabel }) => (
                            <MenuItem key={value} value={value}>
                              {optionLabel}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {!partnerDashboardOnly && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={600}>
                    Business Profile Access
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }} disabled={isBusinessProfileAccessDisabled}>
                    <Select
                      value={businessProfilesAccessLevel}
                      onChange={(e) => handleAccessChange(BUSINESS_PROFILES_MODULE_KEY, e.target.value)}
                      sx={{ '& .MuiSelect-select': { py: 0.5, fontSize: '0.875rem' } }}
                    >
                      {BUSINESS_PROFILE_ACCESS_LEVEL_OPTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select the business profiles this role can access. At least one profile is required unless Developer
                  Dashboard is enabled.
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <BusinessProfileMultiSelect
                    profiles={profiles}
                    selected={allowedBusinessProfileIds}
                    isLoading={isProfilesLoading}
                    disabled={isBusinessProfileSelectDisabled}
                    onChange={handleBusinessProfileIdsChange}
                    label="Business Profiles"
                    required={isBusinessProfilesRequired}
                    error={showBusinessProfilesError}
                    helperText={
                      showBusinessProfilesError
                        ? 'At least one Business Profile must be selected to create a role.'
                        : undefined
                    }
                    allProfilesLabel="No Business Profiles Selected"
                  />
                  <SelectedItems
                    items={allowedBusinessProfileIds}
                    getLabel={(profileId) => getProfileChipLabel(profiles, profileId)}
                    onRemove={(profileId) =>
                      handleBusinessProfileIdsChange(allowedBusinessProfileIds.filter((id) => id !== profileId))
                    }
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Box>

        {!isView && (
          <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1 }}>
            <Button onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isSubmitDisabled}>
              {isPending ? 'Processing...' : isCreate ? 'Create Role' : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Paper>
    </Modal>
  );
}
